import {
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import { localize } from "client_legacy/ndaw/culture";
import {
	InterestedZaaksysteemParty,
	ZaaksysteemLiveInformationProvider
} from "client_legacy/Workspace/LiveInformation/Zaaksysteem/provider";
import { Guid } from "client_legacy/ndaw/guid";
import { TileContext } from "../TileContext";
import { TileTabProps } from "../TileTabSelector";
import { BusyIndicator, ChangeTriggerKind } from "client/Components";
import {
	ItemsLiveInformationResponseModel,
	ZaaksysteemCaseListItemModel,
	ZaaksysteemCaseStatus,
	ZaaksysteemInterests,
	ZaaksysteemResourceKind,
	getMyCases
} from "client/Api/Workspace/LiveInformation/Zaaksysteem/zaaksysteemLiveInformation";
import { never } from "client_legacy/ndaw/errors";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { DestinationWindow } from "client/Api/Apps/common";
import { ZaaksysteemListItem } from "./ZaaksysteemListItem";
import { DimensionToRender } from "../../RenderState/common";
import { useStableImperativeCallback } from "client/Hooks";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { ZaaksysteemTabDisplayModel } from "client/Api/Workspace/workspace";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type ZaaksysteemLiveTileContentProps = {
	tabs: readonly ZaaksysteemTabDisplayModel[];
	provider: ZaaksysteemLiveInformationProvider;
	destinationWindow: DestinationWindow;
	connectionId: number;
	onNavigate(): void;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const ZaaksysteemLiveTileContent = (props: ZaaksysteemLiveTileContentProps): ReactElement => {
	const {
		tabs: tabsFromProps,
		provider,
		destinationWindow,
		connectionId,
		onNavigate,
		onOpenTileMenu
	} = props;

	const {
		id: tileId,
		icon,
		name,
		dimension,
		environmentAppId,
		interactionState,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const [activeTabId, setActiveTabId] = useState<Guid | undefined>(
		(): Guid | undefined => tabsFromProps[0]?.uniqueId
	);

	const [tabs, setTabs] = useState<readonly ZaaksysteemTab[]>(
		() => tabsFromProps.map((x): ZaaksysteemTabDisplayModel => ({
			uniqueId: x.uniqueId,
			name: x.name,
			resourceKind: x.resourceKind
		}))
	);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const countChangeHandler = useCallback(
		(tabId: Guid, count: number): void => {
			setTabs(
				(x) => x.map(
					(x): ZaaksysteemTab => {
						if (x.uniqueId === tabId) {
							return {
								...x,
								count,
								suffix: undefined
							};
						}

						return x;
					}
				)
			);
		},
		[]
	);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigate();
		},
		[onNavigate, addAnalyticsEvent]
	);

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header icon={icon}>
				<LiveTileContent.TabSelector
					value={activeTabId}
					onChange={setActiveTabId}
					tabs={tabs}
					tileId={tileId}
					tileName={name}
				/>
			</LiveTileContent.Header>

			<LiveTileContent.Content>
				{tabs.map(x => (
					<Tab
						key={x.uniqueId}
						tabId={x.uniqueId}
						isActive={x.uniqueId === activeTabId}
						connectionId={connectionId}
						onItemsCountChange={count => countChangeHandler(x.uniqueId, count)}
						provider={provider}
						resourceKind={x.resourceKind}
						destinationWindow={destinationWindow}
						dimension={dimension}
						addAnalyticsEvent={addAnalyticsEvent}
					/>
				))}
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={name}
				onClick={navigateHandler}
			/>
		</LiveTileContent>
	);
};
ZaaksysteemLiveTileContent.displayName = "ZaaksysteemLiveTileContent";

type ZaaksysteemTab = TileTabProps & {
	resourceKind: ZaaksysteemResourceKind;
};

type TabProps = {
	tabId: Guid;
	isActive: boolean;
	connectionId: number;
	resourceKind: ZaaksysteemResourceKind;
	provider: ZaaksysteemLiveInformationProvider;
	destinationWindow: DestinationWindow;
	dimension: DimensionToRender;
	onItemsCountChange(
		count: number
	): void;
	addAnalyticsEvent(): void;
};

const Tab = (props: TabProps): ReactElement | null => {
	const {
		tabId,
		isActive,
		connectionId,
		resourceKind,
		provider,
		destinationWindow,
		dimension,
		onItemsCountChange,
		addAnalyticsEvent
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [requestedPage, setRequestedPage] = useState<number>(0);
	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const stableOnItemsCountChanged: typeof onItemsCountChange= useStableImperativeCallback(onItemsCountChange);
	const loadMoreCancellationRef: RefObject<AbortController | null> =
		useRef<AbortController>(null);
	const searchCancellationRef: RefObject<AbortController | null> =
		useRef<AbortController>(null);

	const shouldUseWideTemplate: boolean = dimension.kind === "normal"
		&& dimension.dimension
			.columnSpan > 4;


	const party = useMemo(
		(): InterestedZaaksysteemParty => ({
			partyId: tabId,
			resourceId: {
				connectionId,
				resourceKind
			},
			resourceKind,
			updateItems: (items, hasMore) => {
				setLoadingState({
					isLoading: false,
					response: {
						items,
						hasMore
					}
				});
			},
			updateCount: (count) => {
				stableOnItemsCountChanged(count);
			},
			informAboutGlobalRetrievalFailure: () => {
				setLoadingState({
					isLoading: false,
					hasGlobalFailure: true
				});
			},
			informAboutConsecutiveErrors: () => {
				setLoadingState({
					isLoading: false,
					hasTooManyConsecutiveErrors: true
				});
			},
			informAboutAccessViolation: () => {
				setLoadingState({
					isLoading: false,
					hasNoAccess: true
				});
			}
		}),
		[
			connectionId,
			stableOnItemsCountChanged,
			resourceKind,
			tabId
		]
	);

	const interests = useMemo(
		(): ZaaksysteemInterests => {
			return {
				count: true,
				items: isActive
			};
		},
		[isActive]
	);

	const fetchItems = useCallback(
		async (
			searchTerm: string,
			pageToLoad: number,
			signal: AbortSignal,
			prevItems: readonly ZaaksysteemCaseListItemModel[]
		): Promise<void> => {
			setRequestedPage(pageToLoad);

			try {
				setLoadingState({
					isLoading: true,
					prevItems
				});

				const page: ItemsLiveInformationResponseModel = await getMyCases(
					getCaseStatus(resourceKind),
					connectionId,
					pageToLoad,
					searchTerm,
					signal
				);

				setLoadingState({
					isLoading: false,
					response: {
						items: [
							...prevItems,
							...page.items
						],
						hasMore: page.hasMore
					}
				});
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				setLoadingState({
					isLoading: false,
					response: {
						items: prevItems,
						hasMore: false
					}
				});
			}
		},
		[
			connectionId,
			resourceKind
		]
	);

	const loadMoreHandler = useCallback(
		async (
			prevItems: readonly ZaaksysteemCaseListItemModel[],
			requestedPage: number
		): Promise<void> => {
			loadMoreCancellationRef.current
				?.abort();

			const cancellation = new AbortController();
			loadMoreCancellationRef.current = cancellation;
			const pageToLoad: number = requestedPage + 1;

			await fetchItems(
				searchTerm,
				pageToLoad,
				cancellation.signal,
				prevItems
			);
		},
		[
			fetchItems,
			searchTerm
		]
	);

	const searchHandler = useCallback(
		async (
			searchTerm: string
		): Promise<void> => {
			searchCancellationRef.current
				?.abort();

			const cancellation = new AbortController();
			searchCancellationRef.current = cancellation;

			await fetchItems(
				searchTerm,
				0,
				cancellation.signal,
				[]
			);
		},
		[
			fetchItems
		]
	);

	const scrollHandler = useCallback(
		(scrollTop: number): void => {
			if (requestedPage < 1) {
				return;
			}

			if (scrollTop <= 0) {
				loadMoreCancellationRef.current
					?.abort();
				setRequestedPage(0);
			}
		},
		[requestedPage]
	);

	const navigationHandler = useCallback(
		(url: string): void => {
			addAnalyticsEvent();

			navigateTo(
				url,
				destinationWindow === DestinationWindow.NewWindow
			);
		},
		[destinationWindow, addAnalyticsEvent]
	);

	const searchTermChangeHandler = useCallback(
		(value: string): void => {
			setSearchTerm(value);
			searchHandler(value);
		},
		[searchHandler]
	);

	useEffect(
		(): (() => void) => {
			provider.register(
				party,
				interests
			);

			return (): void => {
				provider.unregister(party);
			};
		},
		[
			party,
			provider,
			interests
		]
	);

	useEffect(
		(): (() => void) => {
			return (): void => {
				loadMoreCancellationRef.current
					?.abort();

				searchCancellationRef.current
					?.abort();
			};
		},
		[]
	);

	useEffect(
		() => {
			hasImplicitDependencyOn(isActive);
			setSearchTerm("");
			loadMoreCancellationRef.current
				?.abort();

			searchCancellationRef.current
				?.abort();
		},
		[isActive]
	);

	if (!isActive) {
		return null;
	}

	const renderPage = (page: ItemsLiveInformationResponseModel, requestedPage: number): ReactElement => {
		const { items, hasMore } = page;

		if (items.length === 0) {
			const key: LocalizationKey = searchTerm
				? "_NoResultsFound"
				: "_NoItemsToShow";

			return (
				<LiveTileContent.Message
					text={localize(key)}
				/>
			);
		}

		return (
			<>
				<LiveTileContent.List>
					{items.map(x => (
						<ZaaksysteemListItem
							key={x.caseNumber}
							data={x}
							onNavigate={navigationHandler}
							shouldUseWideTemplate={shouldUseWideTemplate}
						/>
					))}
				</LiveTileContent.List>

				{hasMore && (
					<LiveTileContent.LoadMoreButton
						onClick={() => loadMoreHandler(page.items, requestedPage)}
					/>
				)}
			</>
		);
	};

	const renderContent = (): ReactElement => {
		if (loadingState.isLoading) {
			return (
				<>
					{(loadingState.prevItems && loadingState.prevItems.length > 0) && (
						<>
							{renderPage(
								{
								items: loadingState.prevItems,
									hasMore: false
								},
								requestedPage
							)}
						</>
					)}
				</>
			);
		}

		if (loadingState.hasGlobalFailure) {
			return (
				<LiveTileContent.Message
					text={localize("_UnableToRetrieveItems")}
				/>
			);
		}

		if (loadingState.hasNoAccess) {
			return (
				<LiveTileContent.Message
					text={localize("_NoAccess")}
				/>
			);
		}

		if (loadingState.hasTooManyConsecutiveErrors) {
			return (
				<TileRefresh onRefresh={() => provider.forceReload()} />
			);
		}


		return renderPage(
			loadingState.response,
			requestedPage
		);
	};

	return (
		<LiveTileContent.ContentStack>
			<LiveTileContent.SearchInput
				onChange={searchTermChangeHandler}
				placeholder={localize("_Search")}
				trigger={{
					kind: ChangeTriggerKind.Debounced
				}}
			/>
			<LiveTileContent.BusyIndicatorContainer>
				{loadingState.isLoading && (
					<BusyIndicator />
				)}

				<LiveTileContent.Scrollable onScroll={scrollHandler}>
					{renderContent()}
				</LiveTileContent.Scrollable>
			</LiveTileContent.BusyIndicatorContainer>
		</LiveTileContent.ContentStack>
	);
};

Tab.displayName = "Tab";

type LoadingState = {
	isLoading: true;
	prevItems?: readonly ZaaksysteemCaseListItemModel[];
} | {
	isLoading: false;
} & (
		{
			hasNoAccess: true;
			hasGlobalFailure?: undefined;
			hasTooManyConsecutiveErrors?: undefined;
		}
		|
		{
			hasGlobalFailure: true;
			hasNoAccess?: undefined;
			hasTooManyConsecutiveErrors?: undefined;
		}
		|
		{
			hasTooManyConsecutiveErrors: true;
			hasGlobalFailure?: undefined;
			hasNoAccess?: undefined;
		}
		|
		{
			hasNoAccess?: undefined;
			hasGlobalFailure?: undefined;
			hasTooManyConsecutiveErrors?: undefined;
			response: ItemsLiveInformationResponseModel;
		}
	);

function getCaseStatus(resourceKind: ZaaksysteemResourceKind): ZaaksysteemCaseStatus {
	switch (resourceKind) {
		case ZaaksysteemResourceKind.MyOpenCases:
			return ZaaksysteemCaseStatus.Open;
		case ZaaksysteemResourceKind.MySuspendedCases:
			return ZaaksysteemCaseStatus.Stalled;
		default:
			never(resourceKind);
	}
}
